import { Injectable } from '@jkhy/frontend-utils/di';
import {
    CompanyAdminSettingsClient,
    CompanyUserAccountSettingsClient,
    CompanyUserChallengeSettingsClient,
    LoginParametersClient,
    UserApprovalSettingsModelDto,
} from '@treasury/api/bo';
import { AccountSettings } from './entities/account-settings.entity';
import { AdminSettings } from './entities/admin-settings.entity';
import { AuthenticationIdentitySettings } from './entities/authentication-identity-settings.entity';
import { ChallengeSettings } from './entities/challenge-settings.entity';
import { LoginParameters } from './entities/login-parameters.entity';
import { UisSecurityLevel } from './entities/uis-security-level.entity';

@Injectable()
export class CompanyUserSettingsService {
    constructor(
        private readonly adminSettingsClient: CompanyAdminSettingsClient,
        private readonly accountSettingsClient: CompanyUserAccountSettingsClient,
        private readonly challengeSettingsClient: CompanyUserChallengeSettingsClient,
        private readonly loginParametersClient: LoginParametersClient
    ) {}

    public async getAdminSettings(): Promise<AdminSettings> {
        const response = await this.adminSettingsClient.companyAdminSettingsGet();
        const dto = Array.isArray(response.data) ? response.data[0] : response.data;
        return AdminSettings.from(dto ?? ({} as UserApprovalSettingsModelDto));
    }

    public async updateAdminSettings(settings: AdminSettings): Promise<void> {
        await this.adminSettingsClient.companyAdminSettingsApprovalSettings(
            settings.id,
            settings.toDto()
        );
    }

    public async getAccountSettings(): Promise<AccountSettings> {
        const response = await this.accountSettingsClient.companyUserAccountSettingsGet();
        return AccountSettings.from(response.data);
    }

    public async updateAccountSettings(settings: AccountSettings): Promise<void> {
        await this.accountSettingsClient.companyUserAccountSettingsPut(settings.toDto());
    }

    public async getChallengeSettings(): Promise<ChallengeSettings> {
        const response = await this.challengeSettingsClient.companyUserChallengeSettingsGet();
        return ChallengeSettings.from(response.data);
    }

    public async updateChallengeSettings(settings: ChallengeSettings): Promise<void> {
        await this.challengeSettingsClient.companyUserChallengeSettingsPut(settings.toDto());
    }

    public async getUisSecurityLevels(): Promise<UisSecurityLevel[]> {
        const response =
            await this.challengeSettingsClient.companyUserChallengeSettingsGetUisSecurityLevel();
        return (response.data ?? []).map(dto => UisSecurityLevel.from(dto));
    }

    public async getAuthenticationSettings(): Promise<AuthenticationIdentitySettings> {
        const response =
            await this.challengeSettingsClient.companyUserChallengeSettingsGetInstitutionUisIdentitySettings();
        return AuthenticationIdentitySettings.from(response.data);
    }

    public async saveAuthenticationSettings(
        settings: AuthenticationIdentitySettings
    ): Promise<void> {
        await this.challengeSettingsClient.companyUserChallengeSettingsSaveInstitutionUisIdentitySettings(
            settings.toDto()
        );
    }

    public async getLoginParameters(): Promise<LoginParameters> {
        const response = await this.loginParametersClient.loginParametersGet({ type: 'company' });
        return LoginParameters.from(response.data);
    }

    public async updateLoginParameters(params: LoginParameters): Promise<LoginParameters> {
        const response = await this.loginParametersClient.loginParametersPut(
            params.id,
            params.toDto()
        );
        return LoginParameters.from(response.data);
    }

    public async revertLoginParametersToDefaults(id: number): Promise<LoginParameters> {
        const response = await this.loginParametersClient.loginParametersDefaults(id, {
            isCompanyParameter: true,
        });
        return LoginParameters.from(response.data);
    }
}
